import React from 'react';
import clsx from 'clsx';
import { useToast } from '@context/toast';
import {
  GetUserNotificationPreferencesDocument,
  invalidateOperations,
  UpdateNotificationEmailDocument,
  useMutation,
} from '@graphql/reactQuery';
import { useStableCallback } from '@hooks/useStableCallback';
import { useEmailNotifications } from './hooks';

export function ResendVerificationEmail({
  isError,
  confirmationModal,
}: {
  isError?: boolean;
  confirmationModal?: boolean;
}) {
  const { closeToast, openToast } = useToast();
  const { email } = useEmailNotifications();

  const { mutateAsync, isLoading } = useMutation(UpdateNotificationEmailDocument, {});

  const handleResend = useStableCallback(async () => {
    const { data } = await mutateAsync({ email });

    closeToast();

    if (data?.updateNotificationEmail.__typename === 'MutationUpdateNotificationEmailSuccess') {
      invalidateOperations({
        operations: [GetUserNotificationPreferencesDocument],
      });

      openToast({
        text: 'Verification email sent',
        variant: 'success',
      });
    } else if (
      data?.updateNotificationEmail.__typename === 'NotificationEmailAlreadyClaimedError'
    ) {
      openToast({
        text: data.updateNotificationEmail.message,
        variant: 'error',
      });
    } else {
      // TODO: linear.app/sound/issue/ENG-9185/add-missing-observability-in-fe
      openToast({
        text: 'Unknown error updating notification email',
        variant: 'error',
      });
    }
  });

  return (
    <button
      type="button"
      className={clsx(
        'font-base underline duration-75 disabled:cursor-not-allowed hover:text-black',
        confirmationModal
          ? 'inline-flex text-base-l font-bold text-base800 '
          : 'text-base-s font-medium text-base600',
        isError || confirmationModal ? 'lowercase' : 'capitalize',
      )}
      onClick={e => {
        e.preventDefault();
        handleResend();
      }}
      disabled={isLoading}
    >
      {confirmationModal ? 'resend verification' : 'resend verification email'}
    </button>
  );
}
